import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import { BannerStyle } from '../../../typings/enums';
import { useAsyncStatus, useOnMount } from '../../hooks';
import * as api from '../../redux/api';
import Accordion from '../generic/Accordion';
import Banner from '../generic/Banner';
import Card from '../generic/Card/Card';
import Loader from '../generic/Loader';
import SimpleReactTable from '../generic/SimpleReactTable';
import TableHeader from '../generic/TableHeader';
import type { FailedFetchResponse } from '../../helpers/errors';

type OnPremOrg = {
  orgName: string;
  tokenName: string;
  coreVersion: string;
  vulnsCliVersion: string;
  helmVersion: string;
  coreMigration: string;
  lastUpdatedAt: string;
};

type Props = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

const OnPremOrgsComponent: VoidFunctionComponent<Props> = ({ isOpen, onToggle }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const [orgList, setOrgList] = useState<OnPremOrg[]>([]);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  useOnMount(async () => {
    onPending();
    try {
      const { data } = await api.get<OnPremOrg[]>('/api/internal-tools/onprem-org-data');
      setOrgList(data ?? []);
      onSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  });

  let table = <Loader />;
  if (status.error) {
    table = (
      <Banner
        isDismissible
        title="Failed to load on-prem org data"
        body={String(status.error)}
        bannerStyle={BannerStyle.High}
      />
    );
  } else if (!status.loading && orgList.length === 0) {
    table = (<p>No on-prem organizations to report.</p>);
  } else if (orgList[0]) {
    const columns: Column[] = Object.keys(orgList[0])
      .map((column): Column => ({
        id: column,
        Header: (<TableHeader title={column} />),
        accessor: column !== 'lastUpdatedAt' ? column : (d) => new Date(d.lastUpdatedAt).toDateString(),
        sortMethod: column !== 'lastUpdatedAt' ? undefined : (a, b) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          if (dateA > dateB) {
            return 1;
          }
          if (dateA < dateB) {
            return -1;
          }
          return 0;
        },
      }));

    table = (<SimpleReactTable
      data={orgList}
      columns={columns}
      // We're not sorting the data directly. The table can manage sort without this
      onSortedChanged={_.noop}
      defaultSortMode={[{ id: 'name', desc: false }]}
      showPagination
      isSortable
      />);
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="onprem-org-versions" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>On Prem Org Versions</Accordion.Target>
          <Accordion.Body>
            {table}
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default OnPremOrgsComponent;
